import React from 'react'
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import backgroundTrees from '../../assets/images/1.webp'
import logoLeft from '../../assets/images/unl/logo-arbol-ec.png'
import logoRight from '../../assets/images/unl/logo-unl-white.png'
import {
  loadMaintenanceConfig,
  normalizeMaintenanceConfig,
} from '../../utils/maintenance'

const MaintenanceOverlay = () => {
  const theme = useTheme()
  const active = useSelector(state => state.serverStatus.maintenance.active)
  const storeConfig = useSelector(state => state.serverStatus.maintenance.config)
  const [remoteConfig, setRemoteConfig] = React.useState(null)

  React.useEffect(() => {
    if (!active || storeConfig) return undefined

    let mounted = true
    loadMaintenanceConfig().then(config => {
      if (mounted) setRemoteConfig(config)
    })

    return () => {
      mounted = false
    }
  }, [active, storeConfig])

  const config = React.useMemo(
    () => normalizeMaintenanceConfig(storeConfig || remoteConfig),
    [remoteConfig, storeConfig],
  )

  if (!active) return null

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme => theme.zIndex.modal + 3,
        minHeight: '100vh',
        overflow: 'auto',
        backgroundImage: `linear-gradient(90deg, rgba(12, 35, 66, 0.86), rgba(40, 59, 40, 0.74)), url(${backgroundTrees})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: theme.palette.text.white,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: { xs: 2, sm: 4, md: 7 },
          pt: { xs: 2, sm: 3 },
          gap: 2,
        }}>
        <Box
          component="img"
          src={logoLeft}
          alt="ArbolEc"
          sx={{
            width: { xs: 116, sm: 150, md: 190 },
            maxHeight: { xs: 54, sm: 66, md: 82 },
            objectFit: 'contain',
          }}
        />
        <Box
          component="img"
          src={logoRight}
          alt="Universidad Nacional de Loja"
          sx={{
            width: { xs: 94, sm: 128, md: 170 },
            maxHeight: { xs: 72, sm: 96, md: 128 },
            objectFit: 'contain',
          }}
        />
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 5, md: 7 },
        }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 680,
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.24)',
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            boxShadow: '0 24px 70px rgba(0, 7, 10, 0.28)',
            color: theme.palette.text.primary,
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: 'center',
          }}>
          <BuildCircleIcon
            sx={{
              fontSize: { xs: 54, sm: 64 },
              color: theme.palette.secondary.dark,
              mb: 2,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontWeight: 800,
              fontSize: { xs: '1.85rem', sm: '2.45rem' },
              lineHeight: 1.1,
              mb: 2,
            }}>
            {config.title}
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: '1rem', sm: '1.12rem' },
              lineHeight: 1.7,
              maxWidth: 560,
              mx: 'auto',
            }}>
            {config.message}
          </Typography>

          {config.eta && (
            <Typography
              sx={{
                mt: 3,
                color: theme.palette.primary.main,
                fontWeight: 700,
                fontSize: '1rem',
              }}>
              Tiempo estimado: {config.eta}
            </Typography>
          )}

          <LinearProgress
            sx={{
              mt: 4,
              height: 5,
              borderRadius: 5,
              backgroundColor: 'rgba(12, 35, 66, 0.12)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.secondary.main,
              },
            }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}>
              Reintentar
            </Button>
            {config.contactHref && config.contactLabel && (
              <Button
                component="a"
                href={config.contactHref}
                target="_blank"
                rel="noreferrer"
                variant="outlined"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: 'rgba(12, 35, 66, 0.35)',
                }}>
                {config.contactLabel}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

export default MaintenanceOverlay
